import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Sparkles, X, Send, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'cn-onboarding-concierge-done';

const SCRIPT = [
  "Hi {name} — welcome to CaraLoom. I'm Cara, your personal copilot. I'll be here whenever you need me.\n\nTo make things feel personal: **who are we caring for today — yourself, or someone you love?**",
  "Got it. **What's their first name?** (Just helps me sound less robotic — totally optional.)",
  "Lovely. **What's the biggest thing you need help with right now?** A few examples: bathing, medications, wound care, companionship, hospice support, or 'I'm not sure yet.'",
  "Last one — **how urgent is this?** Today, this week, or you're just planning ahead.",
];

// Hoisted out of the component to satisfy react/no-unstable-nested-components.
const MD_P = ({ children }) => <p className="text-sm mb-2 last:mb-0 leading-relaxed">{children}</p>;
const MD_STRONG = ({ children }) => <strong className="font-semibold text-cn-primary">{children}</strong>;
const MD_COMPONENTS = { p: MD_P, strong: MD_STRONG };

/**
 * OnboardingConcierge — first-run conversational welcome for new patients.
 *
 * 4 warm questions, autosaves answers as Cara memories, then routes to next
 * step. Dismissal is persisted on the user record (server-side) so it does
 * not reappear across devices/sessions; localStorage is used as an instant
 * in-session guard.
 *
 * Close paths (airtight, June 2026):
 *   1. X button (top-right, 44×44px hit target — meets iOS HIG minimum
 *      tap-target; visually 20×20 with negative margin so the design
 *      footprint is unchanged).
 *   2. "Skip for now — I'll set this up later" link in the footer.
 *   3. ESC key (added Jun 2026 — escape hatch if the X is unreachable
 *      on a particular device or covered by a browser chrome element).
 *   4. Backdrop click (added Jun 2026 — only when the click target
 *      is the overlay itself, not inside the modal card).
 *   5. "Take me to my dashboard" button after step 4 completes.
 *
 * Reliability fixes (Jun 2026):
 *   - `setUser({ onboarding_dismissed: true })` is now called BEFORE the
 *     server POST awaits. The useEffect that opens the modal depends on
 *     `[user]`; flipping the local flag synchronously prevents any race
 *     where the modal could re-open mid-flight on a slow network.
 *   - The POST is fire-and-forget — the UI never blocks on server
 *     acknowledgement. `localStorage` + server-side flag are both written.
 *     Either alone is sufficient to keep the modal closed.
 *   - `e.preventDefault()` + `e.stopPropagation()` on the close handler so
 *     a stray parent click handler can never swallow the dismiss.
 */
export const OnboardingConcierge = () => {
  const { user, token, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  // Tri-state: null = unknown / not yet fetched, true = privacy consent
  // accepted, false = NOT yet accepted (ConsentGate is showing). We
  // refuse to open while consent is still pending — Radix Dialog (used
  // by ConsentGate) sets `pointer-events:none` on <body>, which would
  // make THIS modal un-clickable even though it sits at a higher
  // z-index. This was the actual root cause of the recurring "X button
  // doesn't close" bug reported by patients on production (Feb 2026).
  const [consentReady, setConsentReady] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  // Check consent state on user change + listen for the
  // `caraloom:consent-accepted` event ConsentGate fires when the user
  // accepts. Keeps us strictly behind ConsentGate in the modal stack.
  useEffect(() => {
    if (!user || user.role !== 'patient') { setConsentReady(null); return; }
    let active = true;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/consent`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setConsentReady(!!r.data?.accepted);
      } catch (_) {
        // If the consent check fails, fall through to "ready" — better
        // to risk showing the concierge than to lock it out forever on a
        // transient network error.
        if (active) setConsentReady(true);
      }
    };
    check();
    const onAccepted = () => { if (active) setConsentReady(true); };
    window.addEventListener('caraloom:consent-accepted', onAccepted);
    return () => {
      active = false;
      window.removeEventListener('caraloom:consent-accepted', onAccepted);
    };
  }, [user, token]);

  useEffect(() => {
    if (!user || user.role !== 'patient') {
      // Non-patients or signed-out users should never see this modal.
      if (open) setOpen(false);
      return;
    }
    // Active-close guards (Jun 2026 hardening): the previous version of
    // this effect only ever called setOpen(true). That left the modal
    // stuck open whenever it raced ahead of the AuthContext /auth/me
    // refresh — login() returned a user payload without
    // `onboarding_dismissed`, the modal opened, then ~1s later the
    // refreshed user (dismissed:true) arrived but the effect refused to
    // close. Now any of the three "you're done" states actively closes
    // the modal even if it was opened in the race window.
    if (user.profile_complete || user.onboarding_dismissed || localStorage.getItem(DISMISS_KEY) === user.id) {
      if (open) setOpen(false);
      return;
    }
    // Defer to ConsentGate. We do NOT open while privacy consent is
    // still pending — see the consentReady state docstring above for
    // why this matters (Radix Dialog kills our pointer events).
    if (consentReady !== true) {
      if (open) setOpen(false);
      return;
    }
    // First-open guard: only seed the conversation when transitioning
    // from closed → open. Without this guard, if `user` updates after
    // the modal is already open (e.g., AuthContext refetches /auth/me),
    // the effect would reset `messages` back to the first script line
    // and wipe out any answers the user typed.
    if (!open) {
      setOpen(true);
      setMessages([{ role: 'assistant', content: SCRIPT[0].replace('{name}', user.first_name || 'there') }]);
    }
  }, [user, open, consentReady]);

  /** Single source of truth for closing the modal — used by every close path. */
  const finishAndClose = useCallback((e) => {
    // Defensive: stop any parent click handler from preventing the dismiss.
    e?.preventDefault?.();
    e?.stopPropagation?.();

    // Close UI immediately — the synchronous local state update is the
    // primary guarantee that the user sees the modal go away on click.
    setOpen(false);

    if (!user) return;

    // Synchronous local guards FIRST (before any await):
    //   - localStorage so the next render of <OnboardingConcierge/> in
    //     this browser session skips the open-effect.
    //   - setUser so the next useEffect run sees onboarding_dismissed:true
    //     and never calls setOpen(true) again.
    try {
      localStorage.setItem(DISMISS_KEY, user.id);
    } catch (_) {
      /* private-browsing / storage-disabled — server-side flag still persists */
    }
    setUser((u) => (u ? { ...u, onboarding_dismissed: true } : u));

    // Server persistence is fire-and-forget. The UI never waits on this.
    // If it fails (network, auth expiry, etc.) localStorage carries the
    // dismissal through the current session; on next login `auth/me`
    // will re-read the flag from the user record — which is why we issue
    // the POST even though the UI doesn't depend on its success.
    axios
      .post(`${API}/onboarding/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => { /* non-blocking */ });
  }, [user, token, setUser]);

  // ESC-key escape hatch — fires whenever the modal is open. Critical
  // for keyboard accessibility AND as a fallback when a specific device's
  // X button is unreachable (e.g., browser chrome covering the top-right).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') finishAndClose(e); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, finishAndClose]);

  const saveMemory = async (key, value) => {
    try {
      await axios.post(`${API}/lynk/memory`, { key, value, source: 'onboarding' }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { /* non-blocking */ }
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    const userMsg = { role: 'user', content: text };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setBusy(true);

    const memoryKeys = ['care_for_self_or_loved_one', 'patient_first_name', 'biggest_care_need', 'care_urgency'];
    await saveMemory(memoryKeys[step], text);

    await new Promise((r) => setTimeout(r, 350));
    const nextStep = step + 1;
    if (nextStep < SCRIPT.length) {
      setMessages((m) => [...m, { role: 'assistant', content: SCRIPT[nextStep] }]);
      setStep(nextStep);
    } else {
      setMessages((m) => [...m, { role: 'assistant', content: `Thank you, ${user.first_name || 'friend'}. I'll remember all of this — your dashboard is ready below, and I'll be here in the corner whenever you need me. 💚` }]);
      setStep(nextStep);
    }
    setBusy(false);
  };

  if (!open) return null;
  const done = step >= SCRIPT.length;

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up"
      data-testid="onboarding-concierge"
      // Defensive pointer-events override (Feb 2026 hardening):
      // Radix Dialog (used by ConsentGate and others in the UI library)
      // sets `pointer-events:none` on <body> while it's open, which
      // would otherwise cascade down and disable clicks on THIS modal —
      // even though our z-index is higher. Forcing `auto` here re-arms
      // every close path (X button, backdrop click, skip link).
      // We also pick z-140 (> PwaInstallPrompt's z-120 and > Radix's z-50)
      // so we always paint on top when both portals are open.
      style={{ pointerEvents: 'auto' }}
      // Backdrop click escape hatch — only when click target IS the overlay
      // itself (not bubbled from inside the modal card). Combined with the
      // stopPropagation on the inner card, this is safe — typing in the
      // input or clicking the avatar etc. will never dismiss.
      onClick={(e) => { if (e.target === e.currentTarget) finishAndClose(e); }}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl bg-white dark:bg-[#0F172A] border border-cn-border/60 shadow-2xl overflow-hidden flex flex-col max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 text-white" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #334155 60%, #334155 100%)' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Welcome · 60-second setup</p>
                <h3 className="font-display font-extrabold text-xl">Let&apos;s make CaraLoom feel personal</h3>
              </div>
            </div>
            {/*
              X button hit target = 44×44 (iOS HIG / WCAG 2.5.5 minimum) via
              `min-w/h` + flex centering. Visual icon stays 20×20 so the
              design doesn't change. `touch-manipulation` removes the 300ms
              tap-delay on mobile Safari. type="button" so a future <form>
              wrap can't accidentally treat this as a submit.
            */}
            <button
              type="button"
              onClick={finishAndClose}
              aria-label="Close onboarding"
              data-testid="onboarding-skip"
              className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-4 h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full bg-white transition-all duration-500" style={{ width: `${(Math.min(step, SCRIPT.length) / SCRIPT.length) * 100}%` }} />
          </div>
          <p className="text-[11px] text-white/70 mt-1.5">Step {Math.min(step + 1, SCRIPT.length)} of {SCRIPT.length} · everything you tell me stays private and helps me help you faster.</p>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-cn-bg/40 dark:bg-[#020617]/40">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                m.role === 'user' ? 'bg-cn-primary text-white rounded-br-md' : 'bg-white dark:bg-white/5 text-cn-text dark:text-white border border-cn-border/60 rounded-bl-md'
              }`}>
                {m.role === 'assistant' ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown components={MD_COMPONENTS}>{m.content}</ReactMarkdown>
                  </div>
                ) : <p className="whitespace-pre-wrap">{m.content}</p>}
              </div>
            </div>
          ))}
          {busy && <div className="flex items-center gap-2 text-sm text-cn-text-muted px-2"><Loader2 className="w-4 h-4 animate-spin text-cn-primary" /> Cara is saving…</div>}
          <div ref={bottomRef} />
        </div>

        <div className="p-4 border-t border-cn-border/60 bg-white dark:bg-[#0F172A]">
          {done ? (
            <button type="button" onClick={finishAndClose} data-testid="onboarding-done" className="cn-btn cn-btn-primary w-full h-12">
              Take me to my dashboard <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center gap-2 bg-cn-bg dark:bg-white/5 border border-cn-border/60 rounded-2xl p-1.5 focus-within:border-cn-primary/40">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="Type your answer…"
                data-testid="onboarding-input"
                className="flex-1 ml-2 bg-transparent outline-none text-sm text-cn-text dark:text-white"
                disabled={busy}
                autoFocus
              />
              <button type="button" onClick={send} disabled={busy || !input.trim()} data-testid="onboarding-send" className="p-2.5 rounded-xl text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
          <button type="button" onClick={finishAndClose} className="text-[11px] text-cn-text-muted hover:text-cn-text mt-2 mx-auto block" data-testid="onboarding-skip-link">
            Skip for now — I&apos;ll set this up later
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingConcierge;
